import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";

import { CheckCircle } from "lucide-react";

import logo from "../assets/logo_basic.png";

import { FcGoogle } from "react-icons/fc";

import { supabase } from "../supabaseClient";

import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthProvider";

import { useSearchParams } from "react-router-dom";
import AppFooter from "../components/AppFooter";
import {

  getBlogAttribution,

  trackAttributedEventOnce

} from "../utils/blogAnalytics";



export default function Login() {

  const SITE_URL = "https://www.reignitespirit.com";

  //const SITE_URL = "http://localhost:8080";



  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState(false);



  const navigate = useNavigate();

  const goToRegistrationPlans = () => {
  navigate({
    pathname: "/landing",
    hash: "#pricing",
  });
};

  const { t, i18n } = useTranslation();

  const isArabic = i18n.language === "ar";



  // 🔐 AUTH FROM CONTEXT ONLY

  const { session, initialized } = useAuth();



const [params] = useSearchParams();

const err = params.get("err");

const getRedirectAfterLogin = () => {

  // ✅ 1) URL param from ProtectedRoute

  const urlReturn = params.get("return");

  if (urlReturn) return urlReturn;



  // ✅ 2) stored by ProtectedRoute

  const stored = localStorage.getItem("redirect_after_login");

  if (stored) return stored;



  return null;

};

const setOAuthLock = (flow) => sessionStorage.setItem("oauth_lock", flow);

const clearOAuthLock = () => sessionStorage.removeItem("oauth_lock");

const hasOAuthLock = () => !!sessionStorage.getItem("oauth_lock");



useEffect(() => {

  if (err === "not_registered") {

    setError("This Google account is not registered. Please click Register New to create an account.");

  }



  if (err === "already_registered") {

    setError("");

  }

}, [err]);



  /* ==========================================================

     ✅ SAFE LOGIN PAGE INIT

     - NEVER auto-signOut on /login load (it logs out other tabs)az

     - Only signOut for recovery flow

     - Clear sessionStorage only (safe)

     ========================================================== */

useEffect(() => {

  const params = new URLSearchParams(window.location.search);

  const type = params.get("type");



  if (type === "recovery") {

    supabase.auth.signOut().finally(() => {

      window.history.replaceState({}, "", "/login");

    });

  }

}, []);



  /* ==========================================================

     ✅ Clear previous user cached keys when user switches

     (this is fine to keep)

     ========================================================== */

const clearCrossUserStateOnLogin = (newUid) => {

  const prevUid = sessionStorage.getItem("auth_uid");



  if (prevUid && prevUid !== newUid) {

    console.log("👤 Login user switched:", prevUid, "→", newUid);



    // clear previous user session-scoped dashboard keys

    [

      `dashboard_step_${prevUid}`,

      `payment_started_${prevUid}`,

      `payment_assessment_id_${prevUid}`,

      `current_assessment_id_${prevUid}`,

      `consentAccepted_${prevUid}`,

      `payment_handled_${prevUid}`,

      `lemon_success_${prevUid}`,

      `force_results_${prevUid}`,

      `report_generation_started_${prevUid}`,

    ].forEach((k) => sessionStorage.removeItem(k));

  }

// localStorage.setItem("auth_uid", newUid);



  // ✅ track current user in sessionStorage (matches Dashboard)

  sessionStorage.setItem("auth_uid", newUid);



  // ✅ force Step 1 for this user after login

  // sessionStorage.setItem(`dashboard_step_${newUid}`, "1");



  // ✅ optional: remove global legacy keys if you still have them

  [

    "force_results",

    "lemon_success",

    "report_generation_started",

    "current_assessment_id",

    "consentAccepted",

  ].forEach((k) => localStorage.removeItem(k));

};

  /* ==========================================================
     ✅ REDIRECT IF ALREADY AUTHENTICATED
     + PHASE 6C EMAIL-CONFIRMATION / EXISTING-SESSION FALLBACK
     ========================================================== */
useEffect(() => {
  if (hasOAuthLock()) return;
  if (!initialized) return;
  if (!session?.user?.id) return;

  let cancelled = false;

  const finishAuthenticatedLogin = async () => {
    const authUid = session.user.id;

    try {
      const { data: dbUser, error: dbError } = await supabase
        .from("users")
        .select("id,name,is_premium,can_bypass_payment,plan,source,registration_complete")
        .eq("auth_uid", authUid)
        .maybeSingle();

      if (cancelled) return;

      if (dbError) {
        console.warn("[Login] Existing-session DB lookup failed:", dbError);
      }

      const dbPlan =
        dbUser?.is_premium === true || dbUser?.plan === "deep_audit"
          ? "deep_audit"
          : "snapshot";

      if (dbUser) {
        sessionStorage.setItem("db_verified", "1");
        sessionStorage.setItem("user_plan", dbPlan);
        localStorage.setItem("selectedPlan", dbPlan);
        localStorage.setItem(`user_plan_${authUid}`, dbPlan);

        if (dbUser?.source) {
          localStorage.setItem("registrationSource", dbUser.source);
        }
      }

      try {
        const blogAttribution = getBlogAttribution();

        console.log("[Phase6C] Existing authenticated session check:", {
          authUid,
          dbPlan,
          blogAttribution,
        });

        if (blogAttribution?.blogSlug) {
          trackAttributedEventOnce(
            `signup_from_blog_${authUid}`,
            "signup_from_blog",
            {
              ...blogAttribution,
              reportType: dbPlan,
            }
          );

          console.log("[Phase6C] signup_from_blog requested", {
            authUid,
            blogSlug: blogAttribution.blogSlug,
            reportType: dbPlan,
          });
        }
      } catch (analyticsError) {
        console.warn(
          "[Phase6C] Existing-session signup tracking failed:",
          analyticsError
        );
      }

      const redirectTo = getRedirectAfterLogin();

      if (redirectTo) {
        localStorage.removeItem("redirect_after_login");
        navigate(redirectTo, { replace: true });
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error(
        "[Login] Existing authenticated session processing failed:",
        error
      );
      navigate("/dashboard", { replace: true });
    }
  };

  finishAuthenticatedLogin();

  return () => {
    cancelled = true;
  };
}, [initialized, session, navigate]);

/* ==========================================================

     ✅ VALIDATION

     ========================================================== */

  const validate = () => {

    if (!email.trim()) return "Email is required.";

    if (!/\S+@\S+\.\S+/.test(email)) return "Please enter a valid email.";

    if (!password.trim()) return "Password is required.";

    if (password.length < 6) return "Password must be at least 6 characters.";

    return "";

  };



  /* ==========================================================

     ✅ EMAIL / PASSWORD LOGIN

     ========================================================== */

  const handleEmailLogin = async (e) => {

  e.preventDefault();



  setError("");

  setSuccess(false);



  const validationError =

    validate();



  if (validationError) {

    return setError(

      validationError

    );

  }



  setLoading(true);



  try {

    // =========================================

    // 1. SIGN IN

    // =========================================



    const {

      data: loginData,

      error,

    } =

      await supabase.auth

        .signInWithPassword({

          email,

          password,

        });



    if (error) {

      throw error;

    }



    const user =

      loginData?.user;



    if (!user?.id) {

      throw new Error(

        "Login succeeded but no user was returned."

      );

    }



    const authUid =

      user.id;



    clearCrossUserStateOnLogin(

      authUid

    );



    // =========================================

    // 2. VERIFY APPLICATION DB USER

    // =========================================



    let dbUser =

      null;



    for (

      let i = 0;

      i < 6;

      i++

    ) {

      const {

        data: userData,

        error: dbError,

      } =

        await supabase

          .from("users")

          .select(

            "id,name,is_premium,can_bypass_payment,plan,source,registration_complete"

          )

          .eq(

            "auth_uid",

            authUid

          )

          .maybeSingle();



      if (dbError) {

        console.warn(

          "[Login] DB verification error:",

          dbError

        );

      }



      if (userData) {

        dbUser =

          userData;



        break;

      }



      await new Promise(

        (resolve) =>

          setTimeout(

            resolve,

            500

          )

      );

    }



    if (!dbUser) {

      throw new Error(

        "User logged in but application profile was not found."

      );

    }



    // =========================================

    // 3. RESOLVE REAL PLAN FROM DB

    // =========================================



    const dbPlan =

      dbUser?.is_premium === true ||

      dbUser?.plan ===

        "deep_audit"

        ? "deep_audit"

        : "snapshot";



    sessionStorage.setItem(

      "db_verified",

      "1"

    );



    sessionStorage.setItem(

      "user_plan",

      dbPlan

    );



    localStorage.setItem(

      "selectedPlan",

      dbPlan

    );



    localStorage.setItem(

      `user_plan_${authUid}`,

      dbPlan

    );



    // =========================================

    // 4. PHASE 6C

    // EMAIL-CONFIRMED BLOG SIGNUP FALLBACK

    // =========================================



    try {

      const blogAttribution =

        getBlogAttribution();



      if (

        blogAttribution?.blogSlug

      ) {

        trackAttributedEventOnce(

          `signup_from_blog_${authUid}`,

          "signup_from_blog",

          {

            ...blogAttribution,



            reportType:

              dbPlan,

          }

        );



        console.log(

          "[Phase6C] signup_from_blog checked after email login",

          {

            authUid,

            blogSlug:

              blogAttribution

                .blogSlug,

            reportType:

              dbPlan,

          }

        );

      }

    }

    catch (

      analyticsError

    ) {

      console.warn(

        "[Phase6C] signup_from_blog login tracking failed:",

        analyticsError

      );

    }



    // =========================================

    // 5. PRESERVE SOURCE

    // =========================================



    if (

      dbUser?.source

    ) {

      localStorage.setItem(

        "registrationSource",

        dbUser.source

      );

    }



    setSuccess(true);



    // =========================================

    // 6. REDIRECT

    // =========================================



    const redirectTo =

      getRedirectAfterLogin();



    if (

      redirectTo

    ) {

      localStorage.removeItem(

        "redirect_after_login"

      );



      navigate(

        redirectTo,

        {

          replace:

            true,

        }

      );



      return;

    }



    navigate(

      "/dashboard",

      {

        replace:

          true,

      }

    );

  }

  catch (

    err

  ) {

    setError(

      err?.message ||

      "Login failed"

    );

  }

  finally {

    setLoading(

      false

    );

  }

};

  /* ==========================================================

     ✅ EXPLICIT "USE ANOTHER ACCOUNT" (THIS is where signOut belongs)

     ========================================================== */

  const handleSwitchAccount = async () => {

    setError("");

    setSuccess(false);



    await supabase.auth.signOut(); // ✅ user-triggered only

    sessionStorage.clear();



    // Optional: clear ONLY auth_uid so next login is treated as fresh user

sessionStorage.removeItem("auth_uid");



    navigate("/login", { replace: true });

  };



  /* ==========================================================

     ✅ GOOGLE LOGIN

     ========================================================== */



const handleGoogleLogin = async () => {

  // this means: "I am in login flow, don’t auto redirect from /login"

  setOAuthLock("login");

  localStorage.setItem("oauth_flow", "login");



  const redirectTo = getRedirectAfterLogin();

  if (redirectTo) localStorage.setItem("redirect_after_login", redirectTo);



  const redirectUrl = `${window.location.origin}/auth/callback?flow=login`;



  await supabase.auth.signInWithOAuth({

    provider: "google",

    options: {

      redirectTo: redirectUrl,

      queryParams: { prompt: "select_account" },

    },

  });

};



const handleGoogleRegister = async () => {

  setOAuthLock("register");

  localStorage.setItem("oauth_flow", "register");



  const redirectUrl = `${window.location.origin}/auth/callback?flow=register`;



  await supabase.auth.signInWithOAuth({

    provider: "google",

    options: {

      redirectTo: redirectUrl,

      queryParams: { prompt: "select_account" },

    },

  });

};











  /* ==========================================================

     ✅ UI

     ========================================================== */

  return (
  <div
    dir={isArabic ? "rtl" : "ltr"}
    className="min-h-screen flex flex-col bg-[#0F0A24] text-white"
  >
    {/* =====================================================
        AUTH HEADER
    ===================================================== */}
    <header className="border-b border-[#EB6AB5]/20 bg-[#11111f]/95 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        {/* LOGO / BRAND */}
        <button
          type="button"
          onClick={() => navigate("/landing")}
          className="flex items-center gap-3 flex-shrink-0"
          aria-label="Go to Reignite Spirit home page"
        >
          <img
            src={logo}
            alt="Reignite Spirit"
            className="w-11 h-11 object-contain drop-shadow-[0_0_12px_#eb6fbd]"
          />

          <div className="text-left hidden sm:block">
            <div className="text-[#EB6AB5] text-lg font-bold tracking-wide">
              REIGNITE.SPIRIT
            </div>

            <div className="text-[#EB6AB5]/70 text-xs tracking-[0.16em]">
              REKINDLE PURPOSE
            </div>
          </div>
        </button>

        {/* HEADER ACTIONS */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/landing")}
            className="hidden sm:inline-flex px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
          >
            Back to Home
          </button>

          {/* IMPORTANT:
              Register goes to plan selection first */}
          <button
            type="button"
            onClick={goToRegistrationPlans}
            className="px-4 py-2 rounded-lg bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold transition whitespace-nowrap"
          >
            Register
          </button>
        </div>
      </div>
    </header>

    {/* =====================================================
        LOGIN CONTENT
    ===================================================== */}
    <main className="flex-1 flex items-center justify-center px-6 py-12">
      <form
        onSubmit={handleEmailLogin}
        className="bg-[#11111f] border border-[#EB6AB5]/30 p-8 sm:p-10 rounded-3xl w-full max-w-md shadow-2xl"
      >
        {/* LOGO INSIDE LOGIN CARD */}
        <div className="text-center mb-6">
          <img
            src={logo}
            className="w-20 mx-auto mb-4 drop-shadow-[0_0_12px_#eb6fbd]"
            alt="Reignite Spirit Logo"
          />

          <h1 className="text-3xl text-[#EB6AB5]">
            {t("auth.loginTitle")}
          </h1>
        </div>

        {/* =================================================
            ALREADY SIGNED IN
        ================================================= */}
        {session && (
          <div className="mb-6 text-center">
            <p className="text-sm text-gray-300 mb-3">
              You’re already signed in.
              Redirecting to your dashboard...
            </p>

            <button
              type="button"
              onClick={handleSwitchAccount}
              className="
                px-4
                py-2
                rounded-full
                bg-gray-700
                hover:bg-gray-600
                text-white
                text-sm
                transition
              "
            >
              Use another account
            </button>
          </div>
        )}

        {/* EMAIL */}
        <input
          type="email"
          placeholder={t("auth.email")}
          value={email}
          onChange={(e) =>
            setEmail(
              e.target.value
            )
          }
          className="
            w-full
            p-3
            mb-3
            rounded-lg
            bg-[#11111f]
            border
            border-[#EB6AB5]/30
            focus:border-[#EB6AB5]
            outline-none
            transition
          "
        />

        {/* PASSWORD */}
        <input
          type="password"
          placeholder={t("auth.password")}
          value={password}
          onChange={(e) =>
            setPassword(
              e.target.value
            )
          }
          className="
            w-full
            p-3
            mb-4
            rounded-lg
            bg-[#11111f]
            border
            border-[#EB6AB5]/30
            focus:border-[#EB6AB5]
            outline-none
            transition
          "
        />

        {/* EXISTING GOOGLE ACCOUNT MESSAGE */}
        {err === "already_registered" && (
          <p className="text-red-400 text-sm mb-3">
            This Google account is already registered.
            Please login instead.
          </p>
        )}

        {/* LOGIN ERROR */}
        {error && (
          <p className="text-red-400 text-sm mb-3">
            {error}
          </p>
        )}

        {/* FORGOT PASSWORD */}
        <button
          type="button"
          onClick={() =>
            navigate(
              "/forgot-password"
            )
          }
          className="text-sm text-pink-400 hover:underline text-center w-full mb-4"
        >
          Forgot Password?
        </button>

        {/* LOGIN BUTTON */}
        <button
          type="submit"
          disabled={loading}
          className="
            w-full
            py-3
            bg-[#EB6AB5]
            hover:bg-[#ff83d1]
            disabled:opacity-60
            disabled:cursor-not-allowed
            text-black
            font-semibold
            rounded-full
            transition
          "
        >
          {loading
            ? t(
                "auth.loggingIn"
              )
            : t(
                "auth.loginButton"
              )}
        </button>

        {/* OR */}
        <div className="flex items-center gap-4 my-6">
          <div className="h-px flex-1 bg-white/15" />

          <span className="text-white/70 text-sm">
            {t("auth.or")}
          </span>

          <div className="h-px flex-1 bg-white/15" />
        </div>

        {/* GOOGLE LOGIN */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          className="
            w-full
            flex
            items-center
            justify-center
            gap-2
            py-3
            bg-white
            hover:bg-gray-100
            text-black
            rounded-full
            transition
          "
        >
          <FcGoogle />

          {t(
            "auth.continueWithGoogle"
          )}
        </button>

        {/* =================================================
            REGISTER / NEW USER
        ================================================= */}
        <div className="mt-7 pt-6 border-t border-white/10 text-center">
          <p className="text-sm text-white/60">
            New to Reignite Spirit?
          </p>

          <button
            type="button"
            onClick={goToRegistrationPlans}
            className="
              mt-2
              text-[#EB6AB5]
              font-semibold
              hover:text-[#ff83d1]
              underline
              underline-offset-4
              transition
            "
          >
            Choose your plan and register
          </button>
        </div>

        {/* MOBILE HOME LINK */}
        <button
          type="button"
          onClick={() =>
            navigate(
              "/landing"
            )
          }
          className="sm:hidden mt-6 text-sm text-white/45 hover:text-white w-full text-center transition"
        >
          ← Back to Home
        </button>
      </form>
    </main>

    {/* =====================================================
        SAME FOOTER AS FAQ / PUBLIC PAGES
    ===================================================== */}
    <AppFooter
      variant="landing"
      logoSrc={logo}
    />

    {/* =====================================================
        LOGIN SUCCESS MODAL
    ===================================================== */}
    <AnimatePresence>
      {success && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
        >
          <div className="bg-[#11111f] border border-[#EB6AB5]/30 p-10 rounded-3xl shadow-2xl">
            <CheckCircle className="text-[#EB6AB5] w-14 h-14 mx-auto" />

            <p className="text-center mt-4 text-[#EB6AB5] font-semibold">
              Login successful
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);
}